<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { TYPES as TASK_TYPES } from './store'
import { ROUTES as TASK_ROUTES } from './router'
import { ROUTES as OWNER_ORG_ROUTES } from '@/pages/organization/organization/organization/owner-organization/router'
import { TYPES as ORG_TYPES } from '@/pages/organization/organization/store'
import { hasPerm } from '@/components/PermissionHelper'
import { axiosThux } from '@/axiosThux'

import ThuxListMixin from '@/components/thux-list/ThuxListMixin'

import TaskHistoryTable from './TaskHistoryTable'
import TaskHistoryCommonFilters from './TaskHistoryCommonFilters'
import TaskCreate from './TaskCreate.vue'
import TaskInlineDetail from './TaskInlineDetail.vue'

export default {
  name: 'TaskHistoryList',
  mixins: [ThuxListMixin],
  props: {
    orgId: { required: true },
    categoryTypeCode: { type: String, default: 'company' },
    parentDetailRouteName: { type: String, default: OWNER_ORG_ROUTES.OWNER_ORGANIZATION_DETAIL },
    parentDetailRouteParams: { type: Object, default: null }
  },
  components: {
    'component-table': TaskHistoryTable,
    'common-filters': TaskHistoryCommonFilters,
    'component-edit': TaskCreate,
    'component-detail': TaskInlineDetail
  },
  data () {
    return {
      TASK_ROUTES,
      title: this.$t('Activities'),
      rolePerm: ['tasks_task_list'],
      fieldName: 'id',
      tableParams: { isTreeMode: true, orgId: this.orgId, profile: this.$store.getters['auth/profile'], maxTreeDepth: 0 },
      maxTreeDepth: 0,
      editParams: {
        organization: this.orgId,
        categoryTypeCode: this.categoryTypeCode,
        assigneeFilters: { filter__contact__organizations__organization_types__category: 'com' }
      },
      commonFiltersProps: { categoryTypeCode: this.categoryTypeCode },
      searchFields: [
        { key: 'filter__name__icontains', placeholder: this.$t('Name'), type: 'text', col: 6 },
        { key: 'filter__category', placeholder: this.$t('Category'), type: 'select', options: [], col: 6 },
        { key: 'filter__tags__name__icontains', placeholder: this.$t('Tags'), type: 'text', col: 6 }
      ]
    }
  },
  watch: {
    isTreeMode: {
      immediate: true,
      handler (val) {
        this.tableParams = {
          isTreeMode: val,
          orgId: this.orgId,
          profile: this.$store.getters['auth/profile'],
          maxTreeDepth: this.maxTreeDepth,
          reloadKey: (this.tableParams && this.tableParams.reloadKey ? this.tableParams.reloadKey : 0) + 1
        }
      }
    },
    maxTreeDepth (val) {
      this.tableParams = { ...this.tableParams, maxTreeDepth: val }
    },
    orgDetail: {
      immediate: true,
      handler (val) {
        this.editParams = {
          organization: this.orgId,
          organizationName: val && val.name,
          categoryTypeCode: this.categoryTypeCode,
          assigneeFilters: { filter__contact__organizations__organization_types__category: 'com' }
        }
        this.$set(this, 'intermediateBreadcrumbs', [
          {
            title: val && val.name ? val.name : this.$t('Organization'),
            routerName: this.parentDetailRouteName,
            routerParams: this.parentDetailRouteParams || { id: this.orgId }
          }
        ])
      }
    },
    orgId (val) {
      this.initForOrg(val)
    },
    '$route.query.openTaskId' (val) {
      this.handleOpenTaskId(val)
    }
  },
  computed: {
    ...mapGetters({
      ...TASK_TYPES.GENERIC.task.task.LIST.GETTERS,
      listFilters: TASK_TYPES.GENERIC.task.task.LIST.GETTERS.filters,
      orgDetail: ORG_TYPES.GENERIC.organization.organization.DETAIL.GETTERS.detail
    }),
    showList () {
      return true
    },
    canSeeActionSelect () {
      return false
    },
    canAddInstance () {
      return hasPerm('tasks_task_create')
    },
    panelTitle () {
      const base = this.$t('Activities')
      return this.orgDetail && this.orgDetail.name ? `${base} - ${this.orgDetail.name}` : base
    },
    hasSidebarFilters () {
      const keys = Object.keys(this.listFilters || {})
      return keys.some((k) => k.indexOf('filter__') === 0)
    },
    isTreeMode () {
      return true
    },
    getPaginationFields () {
      return {
        perPage: this.perPage,
        pageOptions: this.pageOptions,
        page: this.page,
        showPagination: true,
        showFooterPagination: true
      }
    }
  },
  created () {
    this.initForOrg(this.orgId)
    this.handleOpenTaskId(this.$route && this.$route.query && this.$route.query.openTaskId)
    this.loadCategoryOptions()
  },
  methods: {
    loadCategoryOptions () {
      const profile = this.$store.getters['auth/profile'] || 'generic'
      const typeCode = this.categoryTypeCode || 'company'
      axiosThux.get(`/api/v1/task/${profile}/task-category/?filter__status=1&filter__type__code=${typeCode}&page_size=1000`)
        .then((resp) => {
          const results = (resp.data && resp.data.results) || []
          const options = results.map((c) => ({ value: c.id, text: c.name }))
          options.unshift({ value: null, text: '' })
          const idx = this.searchFields.findIndex((f) => f.key === 'filter__category')
          if (idx !== -1) this.$set(this.searchFields[idx], 'options', options)
        })
        .catch(() => {})
    },
    ...mapMutations({
      setSelectedList: TASK_TYPES.GENERIC.task.task.LIST.MUTATIONS.setSelectedList,
      setSelectAll: TASK_TYPES.GENERIC.task.task.LIST.MUTATIONS.setSelectAll
    }),
    ...mapActions({
      ...TASK_TYPES.GENERIC.task.task.LIST.ACTIONS,
      retrieveOrganization: ORG_TYPES.GENERIC.organization.organization.DETAIL.ACTIONS.retrieve
    }),
    initForOrg (orgId) {
      this.cleanAllData()
      if (orgId) {
        this.retrieveOrganization({ id: orgId })
        this.fetchMaxTreeDepth()
      }
      this.tableParams = {
        ...this.tableParams,
        orgId,
        reloadKey: (this.tableParams && this.tableParams.reloadKey ? this.tableParams.reloadKey : 0) + 1
      }
      this.getComponentList()
    },
    handleOpenTaskId (openTaskId) {
      if (openTaskId) {
        this.$nextTick(() => {
          this.openDetailForm(parseInt(openTaskId, 10))
        })
      }
    },
    openEditForm (parentItem) {
      const parent = (parentItem && typeof parentItem === 'object') ? parentItem.id : undefined
      this.$set(this, 'editParams', { ...this.editParams, parent })
      this.$set(this, 'componentId', null)
      this.$set(this, 'showEditForm', true)
      this.$set(this, 'showDetailForm', false)
    },
    openDetailForm (payload, isSubtaskCreation) {
      if (isSubtaskCreation && payload && typeof payload === 'object') {
        // bottone "tree": apre TaskEdit in creazione di sotto-attività dall'item
        this.$set(this, 'detailParams', {
          isSubtaskCreation: true,
          item: payload,
          categoryTypeCode: this.categoryTypeCode,
          onSaved: () => this.reloadList()
        })
        this.$set(this, 'componentId', null)
      } else {
        this.$set(this, 'detailParams', { onSaved: () => this.reloadList() })
        this.$set(this, 'componentId', payload)
      }
      this.$set(this, 'showDetailForm', true)
      this.$set(this, 'showEditForm', false)
    },
    buildFilters (filters) {
      const base = { status: 1, organization: this.orgId, parent__isnull: true }
      const merged = Object.assign(base, filters || {})
      if (merged.filter__state == null) {
        merged.filter__state__is_final = 'False'
      }
      return merged
    },
    getComponentList () {
      return this.setFilters(this.buildFilters())
    },
    reloadList () {
      this.getComponentList()
      this.fetchMaxTreeDepth()
      this.tableParams = {
        ...this.tableParams,
        reloadKey: (this.tableParams.reloadKey || 0) + 1
      }
    },
    closeEditForm () {
      this.$set(this, 'showDetailForm', false)
      this.$set(this, 'showEditForm', false)
      this.reloadList()
    },
    closeDetailForm () {
      this.closeEditForm()
    },
    addFiltersToInstance (filters) {
      return this.setFilters(this.buildFilters(filters))
    },
    removeFiltersToInstance () {
      return this.setFilters(this.buildFilters())
    },
    fetchMaxTreeDepth () {
      const profile = this.$store.getters['auth/profile']
      return axiosThux.get(`/api/v1/task/${profile}/visible-task/tree-depth/?organization=${this.orgId}`)
        .then((response) => {
          const value = response.data && response.data.max_tree_depth
          this.maxTreeDepth = typeof value === 'number' ? value : 0
        })
        .catch(() => {
          this.maxTreeDepth = 0
        })
    }
  }
}

export { hasPerm }
</script>
